import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldPlus,
  CircleDollarSign,
  Lightbulb,
  Zap,
  Check,
  ShieldCheck,
  HeartPulse,
  BadgeCheck,
  HandCoins,
  MapPin,
  ChevronDown,
} from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import ContactForm from "@/components/site/ContactForm";
import Reveal from "@/components/site/Reveal";
import VideoSection from "@/components/site/VideoSection";
import ExitIntent from "@/components/site/ExitIntent";
import { PROGRAMS } from "@/data/programs";
import { openCalendly } from "@/lib/constants";
import {
  HERO,
  PROBLEM,
  SOLUTION,
  PROGRAMS as PID,
  HOW,
  TESTIMONIALS,
  TRUST,
  FINAL_CTA,
  CONTACT,
} from "@/constants/testIds";

const scrollTo = (id) => (e) => {
  e.preventDefault();
  document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
};

const ICONS = { ShieldPlus, CircleDollarSign, Lightbulb, Zap };
const PROGRAM_IMAGES = {
  "wellness-plan": "/images/program-wellness.png",
  "tariff-refund": "/images/program-tariff.png",
  "rd-tax-credits": "/images/program-rnd.png",
  "power-plan": "/images/program-power.png",
};

/* ---------- Hero (cinematic full-bleed) ---------- */
function Hero() {
  return (
    <section
      id="top"
      data-testid={HERO.root}
      className="hero"
    >
      <div
        className="hero__bg"
        style={{ backgroundImage: 'url("/images/hero.png")' }}
      />
      <div className="hero__overlay" />
      <motion.div
        className="container-narrow"
        style={{ textAlign: "center", maxWidth: 780, position: "relative", zIndex: 1 }}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.4, 0, 0.2, 1] }}
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="inline-flex items-center gap-2 mb-6"
          style={{
            padding: "6px 14px",
            borderRadius: 999,
            background: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.18)",
            fontSize: 12,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.9)",
            fontWeight: 500,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              background: "var(--color-success)",
              boxShadow: "0 0 8px var(--color-success)",
            }}
          />
          Results-based partnership · No upfront costs
        </motion.div>

        <h1
          className="type-display-hero"
          data-testid={HERO.headline}
          style={{ color: "#ffffff", margin: "0 auto 20px", maxWidth: 780 }}
        >
          Offering Solutions to Improve Your Bottom Line.
        </h1>
        <p
          className="type-body-large hero__sub"
          style={{ maxWidth: 620, margin: "0 auto 40px" }}
        >
          Compliant tax strategies and employee benefits that pay for themselves —
          no upfront costs, no disruption to operations.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            type="button"
            data-testid={HERO.ctaPrimary}
            className="btn-primary btn-primary--invert btn-primary--lg"
            onClick={openCalendly}
          >
            See If You Qualify
          </button>
          <a
            href="#programs"
            data-testid={HERO.ctaSecondary}
            className="btn-ghost btn-ghost--invert"
            onClick={scrollTo("#programs")}
          >
            View Our Programs
          </a>
        </div>

        <div
          data-testid={HERO.metrics}
          className="hero__metrics mt-10 flex flex-wrap items-center justify-center gap-x-4 gap-y-2"
        >
          <span>200+ businesses served</span>
          <span aria-hidden="true" style={{ opacity: 0.5 }}>•</span>
          <span>$18M+ recovered</span>
          <span aria-hidden="true" style={{ opacity: 0.5 }}>•</span>
          <span>98% client retention</span>
        </div>
      </motion.div>

      <div className="hero__scroll hidden md:flex" aria-hidden="true">
        <span>Scroll</span>
        <ChevronDown size={16} />
      </div>
    </section>
  );
}

/* ---------- Problem (image-topped cards) ---------- */
function ProblemSection() {
  const items = [
    {
      testid: PROBLEM.cardOverpay,
      image: "/images/problem-taxes.png",
      alt: "Tax documents and calculator on desk",
      title: "Overpaying Payroll Taxes",
      body: "Hidden deductions go unclaimed year after year, draining cash flow without your knowledge.",
    },
    {
      testid: PROBLEM.cardCosts,
      image: "/images/problem-costs.png",
      alt: "Employee benefits paperwork on office desk",
      title: "Rising Employee Costs",
      body: "Benefits packages that don't retain talent, reduce claims, or improve satisfaction.",
    },
    {
      testid: PROBLEM.cardMissed,
      image: "/images/problem-missed.png",
      alt: "Business analytics dashboard with upward trending graphs",
      title: "Missed Opportunities",
      body: "R&D credits, tariff refunds, and alternative revenue streams sitting idle.",
    },
  ];
  return (
    <section data-testid={PROBLEM.root} className="section-pad">
      <div className="container-wide">
        <Reveal className="max-w-2xl mb-14">
          <div className="eyebrow mb-5">The problem</div>
          <h2 className="type-section-heading">
            Most businesses leave money on the table.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {items.map((it, i) => (
            <Reveal key={it.testid} delay={i * 0.1}>
              <article data-testid={it.testid} className="se-card h-full">
                <div className="se-card__media">
                  <img src={it.image} alt={it.alt} loading="lazy" />
                </div>
                <div className="se-card__body">
                  <h3 className="type-subheading mb-3" style={{ fontSize: 22 }}>
                    {it.title}
                  </h3>
                  <p className="type-body">{it.body}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Solution (dark, glow) ---------- */
function SolutionSection() {
  return (
    <section
      data-testid={SOLUTION.root}
      className="section-pad-lg dark-section"
    >
      <div className="container-narrow" style={{ maxWidth: 820, position: "relative", zIndex: 1 }}>
        <Reveal>
          <div className="eyebrow eyebrow--light mb-6">Our approach</div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="type-section-heading on-dark-heading mb-8" style={{ maxWidth: 720 }}>
            We find the money. You keep it.
          </h2>
        </Reveal>
        <div className="flex flex-col gap-5">
          {[
            "SmartEdge identifies hidden value in payroll, benefits, and tax code. We implement compliant programs that generate measurable savings and improve employee satisfaction.",
            "Our work is results-based — we succeed only when you do. Fully compliant. Non-disruptive. No upfront fees. Services are self-funded through the savings we help unlock.",
            "We've helped businesses across the country reduce payroll taxes, improve retention, and strengthen their bottom line — all without changing existing insurance or operations.",
          ].map((p, i) => (
            <Reveal key={i} delay={0.15 + i * 0.08} y={20}>
              <p
                className="type-body-large"
                style={{ color: "rgba(255,255,255,0.85)", lineHeight: 1.65 }}
              >
                {p}
              </p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.5} className="mt-10">
          <button
            type="button"
            data-testid={SOLUTION.cta}
            className="btn-primary btn-primary--invert"
            onClick={openCalendly}
          >
            Schedule a Free Consultation
          </button>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Programs (asymmetric grid, image cards) ---------- */
function ProgramsSection() {
  return (
    <section id="programs" data-testid={PID.root} className="section-pad">
      <div className="container-wide">
        <Reveal className="max-w-2xl mb-14">
          <div className="eyebrow mb-5">Our programs</div>
          <h2 className="type-section-heading">Four ways we help you win.</h2>
        </Reveal>
        {/* Desktop asymmetric 2x2; bottom row staggered down by 40px */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {PROGRAMS.map((p, i) => {
            const Icon = ICONS[p.icon] || ShieldPlus;
            const stagger = i >= 2 ? "md:mt-10" : "";
            return (
              <Reveal
                key={p.slug}
                delay={(i % 2) * 0.1}
                className={stagger}
              >
                <article
                  data-testid={PID.card(p.slug)}
                  className="se-card h-full"
                >
                  <div className="se-card__media" style={{ aspectRatio: "16 / 9" }}>
                    <img
                      src={PROGRAM_IMAGES[p.slug]}
                      alt={p.title}
                      loading="lazy"
                    />
                  </div>
                  <div className="se-card__body">
                    <div className="flex items-center gap-3 mb-4">
                      <span
                        className="flex items-center justify-center"
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 6,
                          background: "rgba(83,58,253,0.08)",
                          color: "var(--color-purple)",
                        }}
                      >
                        <Icon size={18} strokeWidth={1.5} />
                      </span>
                      <div className="eyebrow" style={{ fontSize: 10, letterSpacing: "2px" }}>
                        {p.tagline?.split(".")[0] || "Program"}
                      </div>
                    </div>
                    <h3
                      className="type-subheading mb-3"
                      style={{ fontSize: 24, letterSpacing: "-0.24px" }}
                    >
                      {p.title}
                    </h3>
                    <p className="type-body mb-6" style={{ flex: 1 }}>
                      {p.body}
                    </p>
                    <Link
                      to={`/programs/${p.slug}`}
                      data-testid={PID.link(p.slug)}
                      className="link-arrow mt-auto"
                      style={{ alignSelf: "flex-start" }}
                    >
                      Learn more →
                    </Link>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- How it works ---------- */
function HowItWorks() {
  const steps = [
    { title: "Discovery Call", body: "We assess your situation and identify opportunities." },
    { title: "Custom Strategy", body: "Tailored programs designed for your business and workforce." },
    { title: "Implementation", body: "Our team handles setup, compliance, and provider coordination." },
    { title: "Ongoing Optimization", body: "We monitor and adjust as your business evolves." },
  ];
  return (
    <section
      id="how-it-works"
      data-testid={HOW.root}
      className="section-pad"
      style={{ background: "var(--color-bg-soft)" }}
    >
      <div className="container-wide">
        <Reveal className="max-w-2xl mb-16">
          <div className="eyebrow mb-5">The process</div>
          <h2 className="type-section-heading">Simple. Compliant. Proven.</h2>
        </Reveal>

        <div className="timeline">
          <div className="hidden md:block timeline__line" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8 relative">
            {steps.map((s, i) => (
              <Reveal
                key={s.title}
                delay={i * 0.12}
                y={20}
                className="flex md:block gap-5 md:gap-0 items-start"
              >
                <motion.div
                  className="timeline__num md:mx-auto"
                  initial={{ scale: 0.6, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{
                    delay: 0.15 + i * 0.12,
                    duration: 0.5,
                    ease: [0.34, 1.56, 0.64, 1],
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </motion.div>
                <div className="md:text-center md:mt-6 flex-1">
                  <h3
                    className="type-subheading mb-2"
                    style={{ fontSize: 18, fontWeight: 500, color: "var(--color-heading)" }}
                  >
                    {s.title}
                  </h3>
                  <p
                    className="type-body"
                    style={{ fontSize: 14, maxWidth: 220, margin: "0 auto" }}
                  >
                    {s.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.4} className="mt-16" style={{ textAlign: "center" }}>
          <button
            type="button"
            data-testid={HOW.cta}
            className="btn-primary"
            onClick={openCalendly}
          >
            Get Started
          </button>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Testimonials ---------- */
function Testimonials() {
  const items = [
    {
      testid: TESTIMONIALS.cardOne,
      quote:
        "After hearing about all the benefits of the program I have to be honest I was skeptical at first because it sounded too good to be true. But now that we're 6 months into the program we are extremely happy we implemented it. We have 220 employees on the program and many of them are following the guidance and improving their health and wellness. The additional tax savings for both our employees and our company is also a great bonus. It has become the top benefit we offer to our employees. We couldn't be happier!",
      name: "Nate C.",
      role: "Office Manager",
      company: "Collins & Bradley Law Group",
      initials: "NC",
    },
    {
      testid: TESTIMONIALS.cardTwo,
      quote:
        "We are in an industry of high employee turnover. It becomes difficult retaining good employees because we sometimes lose them to Fortune 500 companies in our industry that offer these types of incentive programs. We feel fortunate Dan came to us with this program and educated us on how it works and we implemented it right away. Wish we would have had this in place many years ago. Thanks Smartedge.",
      name: "Lanny P.",
      role: "HR Manager",
      company: "Capital City Transport",
      initials: "LP",
    },
  ];
  return (
    <section id="results" data-testid={TESTIMONIALS.root} className="section-pad">
      <div className="container-wide">
        <Reveal className="max-w-2xl mb-14">
          <div className="eyebrow mb-5">Client results</div>
          <h2 className="type-section-heading">What our clients say.</h2>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {items.map((it, i) => (
            <Reveal key={it.testid} delay={i * 0.12}>
              <div data-testid={it.testid} className="quote-card h-full">
                <span className="quote-card__mark" aria-hidden="true">&ldquo;</span>
                <p className="quote-card__quote">{it.quote}</p>
                <div className="flex items-center gap-4 mt-8">
                  <span className="avatar-circle" aria-hidden="true">{it.initials}</span>
                  <div className="flex flex-col">
                    <span className="quote-card__attribution-name">{it.name}</span>
                    <span className="quote-card__attribution-meta">
                      {it.role} · {it.company}
                    </span>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Trust ---------- */
function TrustSection() {
  const badges = [
    { Icon: ShieldCheck, label: "IRS Section 125 Compliant" },
    { Icon: HeartPulse, label: "HIPAA Certified" },
    { Icon: BadgeCheck, label: "Results-Based Pricing" },
    { Icon: HandCoins, label: "$0 Upfront Costs" },
    { Icon: MapPin, label: "Nationwide Coverage" },
  ];
  return (
    <section
      data-testid={TRUST.root}
      className="section-pad"
      style={{ background: "var(--color-bg-soft)" }}
    >
      <div className="container-wide" style={{ textAlign: "center" }}>
        <Reveal>
          <div className="eyebrow mb-5" style={{ justifyContent: "center" }}>
            Trust
          </div>
          <h2
            className="type-section-heading mb-14"
            style={{ margin: "0 auto 56px", maxWidth: 640 }}
          >
            Built on compliance and results.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
          {badges.map((b, i) => (
            <motion.div
              key={b.label}
              initial={{ opacity: 0, y: 24, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{
                delay: i * 0.15,
                duration: 0.55,
                ease: [0.34, 1.56, 0.64, 1],
              }}
              whileHover={{ scale: 1.03, y: -2 }}
              className="trust-tile"
            >
              <span className="trust-tile__icon">
                <b.Icon size={44} strokeWidth={1.5} />
              </span>
              <span className="trust-tile__label">{b.label}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Final CTA ---------- */
function FinalCTA() {
  return (
    <section
      data-testid={FINAL_CTA.root}
      className="section-pad-lg dark-section"
      style={{ textAlign: "center" }}
    >
      <div className="container-narrow" style={{ maxWidth: 720, position: "relative", zIndex: 1 }}>
        <Reveal>
          <h2 className="type-display-large on-dark-heading mb-5">
            Ready to unlock hidden revenue?
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p
            className="type-body-large mb-8"
            style={{ color: "rgba(255,255,255,0.85)" }}
          >
            Book a free consultation. We&apos;ll show you where the money is.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <button
            type="button"
            data-testid={FINAL_CTA.cta}
            className="btn-primary btn-primary--invert btn-primary--lg"
            onClick={openCalendly}
          >
            Schedule Your Call
          </button>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-6 type-caption" style={{ color: "rgba(255,255,255,0.7)" }}>
            Or email us:{" "}
            <a
              href="mailto:info@smartedgebizsolutions.com"
              data-testid={FINAL_CTA.email}
              style={{
                color: "#ffffff",
                textDecoration: "underline",
                textUnderlineOffset: 3,
              }}
            >
              info@smartedgebizsolutions.com
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Contact ---------- */
function ContactSection() {
  return (
    <section id="contact" data-testid={CONTACT.root} className="section-pad">
      <div className="container-wide grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-2">
          <div className="eyebrow mb-5">Get in touch</div>
          <h2 className="type-section-heading mb-5">
            Let&apos;s find your hidden revenue.
          </h2>
          <p className="type-body-large mb-8">
            Tell us a bit about your business. We&apos;ll follow up within one business
            day to schedule a free discovery call — no obligation, no upfront costs.
          </p>
          <div className="flex flex-col gap-4">
            {[
              "Free consultation — no strings attached",
              "Response within one business day",
              "Confidential and never shared",
            ].map((c) => (
              <div key={c} className="flex items-start gap-3">
                <span
                  style={{
                    marginTop: 4,
                    width: 20,
                    height: 20,
                    borderRadius: 999,
                    background: "rgba(21,190,83,0.14)",
                    color: "var(--color-success-text)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Check size={12} strokeWidth={2.5} />
                </span>
                <span className="type-body" style={{ color: "var(--color-label)" }}>{c}</span>
              </div>
            ))}
          </div>
          <hr className="hr-soft my-8" />
          <div className="type-caption" style={{ color: "var(--color-label)", fontWeight: 500 }}>
            Prefer to email or call?
          </div>
          <div className="mt-2 flex flex-col gap-1">
            <a href="mailto:info@smartedgebizsolutions.com" className="type-body">
              info@smartedgebizsolutions.com
            </a>
            <a href="tel:+16142035952" className="type-body">614-203-5952</a>
            <span className="type-caption">Mon – Fri · 9:00 AM – 5:00 PM ET</span>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-3">
          <ContactForm />
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Page ---------- */
export default function Home() {
  return (
    <>
      <Navigation transparentUntilScroll />
      <main>
        <Hero />
        <ProblemSection />
        <SolutionSection />
        <VideoSection />
        <ProgramsSection />
        <HowItWorks />
        <Testimonials />
        <TrustSection />
        <FinalCTA />
        <ContactSection />
      </main>
      <Footer />
      <ExitIntent
        eyebrow="Before you go"
        headline="Grab 15 minutes with Dan."
        body="A short call is the fastest way to see if SmartEdge can unlock hidden revenue in your business. No obligation, no upfront costs."
        ctaLabel="Schedule a 15-min call"
      />
    </>
  );
}
